import React__default from 'react';
import { KeyboardEventKey } from '../../constants.js';
import isEqual from 'lodash.isequal';
import { usePrevious } from '../usePrevious.js';

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

// Direction config options
var UseKeyManagerDirection;

(function (UseKeyManagerDirection) {
  UseKeyManagerDirection["LTR"] = "LTR";
  UseKeyManagerDirection["RTL"] = "RTL";
})(UseKeyManagerDirection || (UseKeyManagerDirection = {}));

var UseKeyManagerOrientation; // Base properties used by this hook when working with items <T> passed to hook

(function (UseKeyManagerOrientation) {
  UseKeyManagerOrientation["Vertical"] = "Vertical";
  UseKeyManagerOrientation["Horizontal"] = "Horizontal";
})(UseKeyManagerOrientation || (UseKeyManagerOrientation = {}));

// Helper class containing methods to traverse the items and manage the current active/focused item
var UseKeyManagerUtilities = /*#__PURE__*/function () {
  function UseKeyManagerUtilities(items, isItemDisabled) {
    _classCallCheck(this, UseKeyManagerUtilities);

    _defineProperty(this, "items", void 0);

    _defineProperty(this, "isItemDisabled", void 0);

    this.items = items;
    this.isItemDisabled = isItemDisabled;
  }

  _createClass(UseKeyManagerUtilities, [{
    key: "getFirstItem",
    value:
    /** Sets the active item to the first enabled item in the list. */
    function getFirstItem() {
      return this.getItemAtIndex(0, 1);
    }
    /** Sets the active item to the last enabled item in the list. */

  }, {
    key: "getLastItem",
    value: function getLastItem() {
      return this.getItemAtIndex(this.items.length - 1, -1);
    }
    /** Sets the active item to the next enabled item in the list. */

  }, {
    key: "getNextItem",
    value: function getNextItem(currentIndex, wrap) {
      return currentIndex < 0 && wrap ? this.getFirstItem() : this.getNextItemByDelta(currentIndex, 1, wrap);
    }
    /** Sets the active item to a previous enabled item in the list. */

  }, {
    key: "getPreviousItem",
    value: function getPreviousItem(currentIndex, wrap) {
      return currentIndex < 0 && wrap ? this.getLastItem() : this.getNextItemByDelta(currentIndex, -1, wrap);
    }
  }, {
    key: "getNextItemByDelta",
    value: function getNextItemByDelta(currentIndex, delta) {
      var wrap = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
      var newItem = null;
      var nextIndex = currentIndex + delta;

      if (wrap === false) {
        // If nextIndex exists, get that item, if there is no item at this index, then we are at the first or last item,
        // so return the current index to keep the active item where it was
        newItem = this.getItemAtIndex(this.items[nextIndex] ? nextIndex : currentIndex, delta);
      } else if (wrap === true) {
        for (var i = 1; i <= this.items.length; i++) {
          var index = (currentIndex + delta * i + this.items.length) % this.items.length; // magic math from: https://github.com/angular/components/blob/main/src/cdk/a11y/key-manager/list-key-manager.ts#L430

          var _item = this.items[index];

          if (!this.isItemDisabled(_item)) {
            newItem = _item;
            break;
          }
        }
      }

      return newItem;
    }
  }, {
    key: "getItemAtIndex",
    value: function getItemAtIndex(i, delta) {
      var index = i; // if index outside list return null

      if (!this.items[index]) {
        return null;
      }

      while (this.isItemDisabled(this.items[index])) {
        index += delta; // if we've reached the end of the list return null

        if (!this.items[index]) {
          return null;
        }
      }

      return this.items[index];
    }
  }]);

  return UseKeyManagerUtilities;
}();
var useKeyManager = function useKeyManager(_ref) {
  var items = _ref.items,
      _ref$wrap = _ref.wrap,
      wrap = _ref$wrap === void 0 ? true : _ref$wrap,
      _ref$direction = _ref.direction,
      direction = _ref$direction === void 0 ? UseKeyManagerDirection.LTR : _ref$direction,
      _ref$orientation = _ref.orientation,
      orientation = _ref$orientation === void 0 ? UseKeyManagerOrientation.Horizontal : _ref$orientation,
      onActiveItemChange = _ref.onActiveItemChange,
      onControlKeyDown = _ref.onControlKeyDown,
      shouldSkipItem = _ref.shouldSkipItem,
      _ref$disabledKeys = _ref.disabledKeys,
      disabledKeys = _ref$disabledKeys === void 0 ? [] : _ref$disabledKeys,
      disableManager = _ref.disableManager;

  var _React$useState = React__default.useState(-1),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      activeItemIndex = _React$useState2[0],
      setActiveItemIndex = _React$useState2[1];

  var _React$useState3 = React__default.useState(null),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      activeItem = _React$useState4[0],
      setActiveItem = _React$useState4[1]; // Need to compare the items with the previous value when they change, so storing them in a ref so they persist


  var previousItems = usePrevious(items);
  React__default.useEffect(function () {
    // only run this if the content of the items have actually changed
    if (!isEqual(previousItems, items)) {
      // if it is still part of the updated items, get the new active item instance
      var newActiveItemInstance = items.find(function (item) {
        return isEqual(item, activeItem);
      }); // Update active item state to match new items set, because it is very likely the index of the item
      // has changed with the updated items object

      if (newActiveItemInstance) {
        setActiveItem(newActiveItemInstance);
        setActiveItemIndex(items.indexOf(newActiveItemInstance));
      } else {
        setActiveItem(null);
        setActiveItemIndex(-1);
      }
    }
  }, [items, activeItem, previousItems]); // Method used to determine if an item is disabled and should be skipped when
  // determining the next active item

  var isItemDisabled = function isItemDisabled(item) {
    // if user added a disabled prop to their items, use that
    if (item.disabled) {
      return true;
    } // If user provided a custom operator, use that to determine if the
    // item is disabled or not


    if (typeof shouldSkipItem === 'function') {
      return shouldSkipItem(item);
    }

    return false;
  }; // Instantiate utility methods with items and filtering function


  var utilities = new UseKeyManagerUtilities(items, isItemDisabled); // Updates state when active item changes
  // note runCallback defaults to false so if a consumer calls `updateActiveItem`
  // there are no side effects from `onActiveChange` running immediately after they just changed it
  // from their end.

  var updateActiveItem = function updateActiveItem(item) {
    var runCallback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var event = arguments.length > 2 ? arguments[2] : undefined;

    if (disableManager) {
      return;
    }

    var previousActiveItem = activeItem;
    var index = typeof item === 'number' ? item : item !== null ? items.indexOf(item) : -1;
    var newActiveItem = items[index]; // If the active item has changed, update state and run callback

    if (!isEqual(previousActiveItem, newActiveItem)) {
      setActiveItem(newActiveItem ? newActiveItem : null);
      setActiveItemIndex(index); // run callback if it exists and if `runCallback` is true

      if (runCallback && typeof onActiveItemChange === 'function') {
        // need to get a fresh copy of the previous item to be able to use `indexOf` below
        var freshPreviousActiveItem = null;

        if (previousActiveItem) {
          freshPreviousActiveItem = items.find(function (item) {
            return isEqual(item, previousActiveItem);
          });
        }

        onActiveItemChange({
          previous: {
            index: !!freshPreviousActiveItem ? items.indexOf(freshPreviousActiveItem) : -1,
            item: previousActiveItem
          },
          active: {
            item: newActiveItem,
            index: index
          },
          event: event
        });
      }
    }
  }; // KeyDown method a user will attach to their container component
  // that will update active item state based on user interaction


  var onKeyDown = function onKeyDown(event) {
    var key = event.key;
    var stop = false;

    switch (key) {
      case KeyboardEventKey.TAB:
        break;

      case KeyboardEventKey.DOWN_ARROW:
        if (orientation === UseKeyManagerOrientation.Vertical) {
          updateActiveItem(utilities.getNextItem(activeItemIndex, wrap), true, event);
          stop = true;
        }

        break;

      case KeyboardEventKey.UP_ARROW:
        if (orientation === UseKeyManagerOrientation.Vertical) {
          updateActiveItem(utilities.getPreviousItem(activeItemIndex, wrap), true, event);
          stop = true;
        }

        break;

      case KeyboardEventKey.RIGHT_ARROW:
        if (orientation !== UseKeyManagerOrientation.Vertical) {
          var nextItem = direction === UseKeyManagerDirection.RTL ? utilities.getPreviousItem(activeItemIndex, wrap) : utilities.getNextItem(activeItemIndex, wrap);
          updateActiveItem(nextItem, true, event);
          stop = true;
        }

        break;

      case KeyboardEventKey.LEFT_ARROW:
        if (orientation !== UseKeyManagerOrientation.Vertical) {
          var _nextItem = direction === UseKeyManagerDirection.RTL ? utilities.getNextItem(activeItemIndex, wrap) : utilities.getPreviousItem(activeItemIndex, wrap);

          updateActiveItem(_nextItem, true, event);
          stop = true;
        }

        break;

      case KeyboardEventKey.HOME:
        if (disabledKeys.indexOf(KeyboardEventKey.HOME) === -1) {
          updateActiveItem(utilities.getFirstItem(), true, event);
          stop = true;
        }

        break;

      case KeyboardEventKey.END:
        if (disabledKeys.indexOf(KeyboardEventKey.END) === -1) {
          updateActiveItem(utilities.getLastItem(), true);
          stop = true;
        }

        break;
    }

    if (stop) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (onControlKeyDown && typeof onControlKeyDown === 'function') {
      onControlKeyDown(event);
    }
  };

  var controlProps = {
    onKeyDown: onKeyDown
  };
  return {
    activeItem: activeItem ? activeItem : null,
    activeItemId: activeItem && activeItem.id ? activeItem.id : null,
    activeItemIndex: activeItemIndex,
    updateActiveItem: updateActiveItem,
    controlProps: controlProps
  };
};

export { UseKeyManagerDirection, UseKeyManagerOrientation, UseKeyManagerUtilities, useKeyManager };
